// Skins #2 (Diamond Hands), #3 (Paper Hands), #4 (Laser Eyes), #5 (Moonboy) builders.

import * as THREE from 'three';
import {
  SEG, darken, lighten, mat, matEmissive, matSkin, enableShadows, buildHumanoid,
  addShortHair, addBuzzCut,
} from './skin-utils';

/* ------------------------------------------------------------------ */
/*  #2 — Diamond Hands: Crystalline warrior with oversized fists       */
/* ------------------------------------------------------------------ */

export function buildDiamondHands(color: number): THREE.Group {
  const group = new THREE.Group();
  const bodyColor = darken(color, 0.4);
  const skinColor = 0xc8b098;

  const body = buildHumanoid({
    bodyColor,
    headColor: skinColor,
    skinColor,
    armColor: bodyColor,
    handColor: color,
    handRadius: 0.035,
    eyeColor: 0x00aaff,
    muscular: true,
  });
  group.add(body);

  // Crystal-infused short hair
  addBuzzCut(group, darken(color, 0.3), 1.66, 0.14);

  // HUGE diamond octahedron fists — signature feature
  for (const side of [-1, 1]) {
    const diamond = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.11, 1),
      mat(color, { roughness: 0.08, metalness: 0.85 }),
    );
    diamond.position.set(side * 0.28, 0.82, 0);
    diamond.rotation.z = Math.PI / 6 * side;
    group.add(diamond);
  }

  // Crystal shoulder pauldrons
  for (const side of [-1, 1]) {
    const crystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.055, 0),
      mat(lighten(color, 1.3), { roughness: 0.08, metalness: 0.9 }),
    );
    crystal.position.set(side * 0.30, 1.46, 0);
    crystal.rotation.z = Math.PI / 4;
    group.add(crystal);
  }

  // Crystal headband/crown
  const headband = new THREE.Mesh(
    new THREE.TorusGeometry(0.15, 0.012, 8, 12),
    mat(color, { roughness: 0.08, metalness: 0.9 }),
  );
  headband.position.set(0, 1.74, 0);
  headband.rotation.x = Math.PI / 2;
  group.add(headband);

  // Crystal chest plate accent
  const chestCrystal = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.04, 0),
    matEmissive(lighten(color, 1.5), 1.0),
  );
  chestCrystal.position.set(0, 1.22, -0.20);
  group.add(chestCrystal);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #3 — Paper Hands: Nervous character with crumbling paper hands     */
/* ------------------------------------------------------------------ */

export function buildPaperHands(color: number): THREE.Group {
  const group = new THREE.Group();
  const skinColor = 0xd8c0a0;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.05,
    torsoScaleZ: 0.6,
    lean: 0.06,
    eyeColor: 0x887766,
  });
  group.add(body);

  // Messy worried hair
  addShortHair(group, darken(color, 0.5), 1.66, 0.14);

  // Worried eyebrow overrides — angled up in center
  for (const side of [-1, 1]) {
    const worriedBrow = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.012, 0.02),
      mat(darken(skinColor, 0.45)),
    );
    worriedBrow.position.set(side * 0.055, 1.72, -0.125);
    worriedBrow.rotation.z = side * 0.3; // angled up = worried look
    group.add(worriedBrow);
  }

  // Flat paper hands — signature feature
  for (const side of [-1, 1]) {
    const paperHand = new THREE.Mesh(
      new THREE.BoxGeometry(0.13, 0.17, 0.018),
      mat(lighten(color, 1.1)),
    );
    paperHand.position.set(side * 0.28, 0.82, 0);
    paperHand.rotation.z = side * 0.3;
    paperHand.rotation.x = 0.15;
    group.add(paperHand);
    // Paper fold lines
    for (let i = 0; i < 3; i++) {
      const fold = new THREE.Mesh(
        new THREE.BoxGeometry(0.10, 0.003, 0.02),
        mat(darken(color, 0.75)),
      );
      fold.position.set(side * 0.28, 0.76 + i * 0.05, -0.01);
      fold.rotation.z = side * 0.3;
      group.add(fold);
    }
  }

  // Tattered paper bits floating around body
  const rng = (seed: number) => Math.sin(seed * 12.9898 + 78.233) * 0.5 + 0.5;
  for (let i = 0; i < 10; i++) {
    const tatter = new THREE.Mesh(
      new THREE.BoxGeometry(0.025 + rng(i) * 0.025, 0.03 + rng(i + 5) * 0.03, 0.006),
      mat(darken(color, 0.7 + rng(i + 10) * 0.3)),
    );
    const angle = (i / 10) * Math.PI * 2;
    const yPos = 0.7 + rng(i + 20) * 0.8;
    tatter.position.set(
      Math.cos(angle) * (0.22 + rng(i + 3) * 0.06),
      yPos,
      Math.sin(angle) * (0.14 + rng(i + 7) * 0.04),
    );
    tatter.rotation.set(rng(i) * 0.5, rng(i + 1) * 0.5, rng(i + 2) * 0.8);
    group.add(tatter);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #4 — Laser Eyes: BTC maximalist with red visor                     */
/* ------------------------------------------------------------------ */

export function buildLaserEyes(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = 0x1a1a2a;
  const skinColor = 0xd4b896;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    armColor: suitColor,
    legColor: suitColor,
    bootColor: 0x111111,
    handColor: skinColor,
    showFace: false, // we add custom face with laser visor
  });
  group.add(body);

  // Slicked-back dark hair
  addShortHair(group, 0x1a1008, 1.66, 0.14);

  // Nose & mouth (manual since showFace=false to avoid normal eyes)
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.012, 0.03, 6), matSkin(darken(skinColor, 0.95)));
  nose.position.set(0, 1.60, -0.135);
  nose.rotation.x = -0.25;
  group.add(nose);
  const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.008, 0.01), mat(darken(skinColor, 0.82)));
  mouth.position.set(0, 1.55, -0.13);
  group.add(mouth);
  // Ears
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.SphereGeometry(0.024, 6, 6), matSkin(darken(skinColor, 0.92)));
    ear.position.set(side * 0.13, 1.67, 0);
    ear.scale.set(0.35, 0.7, 0.5);
    group.add(ear);
  }

  // RED LASER VISOR — the signature feature (covers where eyes would be)
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.04, 0.05),
    matEmissive(color, 3),
  );
  visor.position.set(0, 1.68, -0.135);
  group.add(visor);

  // Visor glow aura
  const aura = new THREE.Mesh(
    new THREE.BoxGeometry(0.26, 0.06, 0.03),
    matEmissive(color, 0.5),
  );
  aura.position.set(0, 1.68, -0.15);
  group.add(aura);

  // BTC orange circle on chest
  const btcCircle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.07, 0.07, 0.015, 16),
    mat(0xff8c00, { roughness: 0.25, metalness: 0.6 }),
  );
  btcCircle.position.set(0, 1.22, -0.19);
  btcCircle.rotation.x = Math.PI / 2;
  group.add(btcCircle);

  // BTC "B" inner detail
  const bBar = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.07, 0.012), mat(0xffffff));
  bBar.position.set(0, 1.22, -0.21);
  group.add(bBar);

  // Formal collar
  for (const side of [-1, 1]) {
    const collar = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.04, 0.02), mat(0xeeeeee));
    collar.position.set(side * 0.06, 1.46, -0.08);
    collar.rotation.z = side * 0.3;
    group.add(collar);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #5 — Moonboy: Astronaut in bulky spacesuit                        */
/* ------------------------------------------------------------------ */

export function buildMoonboy(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = 0xeeeeee;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: suitColor,
    torsoScaleX: 1.45,
    torsoScaleZ: 0.95,
    torsoWidth: 0.20,
    armColor: suitColor,
    legColor: darken(suitColor, 0.92),
    bootColor: 0xcccccc,
    handColor: 0xdddddd,
    handRadius: 0.05,
    shoulderWidth: 0.34,
    showFace: false, // face behind visor
  });
  group.add(body);

  // Fishbowl helmet
  const helmet = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, SEG, SEG),
    mat(0xeeeeee, { roughness: 0.1, metalness: 0.15 }),
  );
  helmet.position.set(0, 1.68, 0);
  group.add(helmet);

  // Dark visor front
  const visor = new THREE.Mesh(
    new THREE.SphereGeometry(0.19, SEG, SEG, -Math.PI * 0.4, Math.PI * 0.8, 0.3, 0.8),
    mat(0x111133, { roughness: 0.05, metalness: 0.8 }),
  );
  visor.position.set(0, 1.68, -0.04);
  group.add(visor);

  // Face visible behind visor (faintly)
  const faceInHelmet = new THREE.Mesh(new THREE.SphereGeometry(0.10, 8, 8), matSkin(0xd4b896));
  faceInHelmet.position.set(0, 1.66, -0.02);
  group.add(faceInHelmet);
  // Eyes behind visor
  for (const side of [-0.03, 0.03]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.012, 6, 6), mat(0x222222));
    eye.position.set(side, 1.67, -0.10);
    group.add(eye);
  }

  // Helmet ring seal
  const seal = new THREE.Mesh(
    new THREE.TorusGeometry(0.20, 0.022, 8, 16),
    mat(0xaaaaaa, { roughness: 0.3, metalness: 0.5 }),
  );
  seal.position.set(0, 1.54, 0);
  seal.rotation.x = Math.PI / 2;
  group.add(seal);

  // Life-support backpack
  const lifePack = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.32, 0.13), mat(0xdddddd));
  lifePack.position.set(0, 1.20, 0.23);
  group.add(lifePack);

  // Orange mission patches
  for (const pos of [
    [0.15, 1.30, -0.22],
    [-0.12, 1.05, -0.20],
    [0.10, 1.10, 0.35],
  ] as [number, number, number][]) {
    const patch = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.035, 0.008), mat(0xff6600));
    patch.position.set(...pos);
    group.add(patch);
  }

  // Crypto moon logo on chest
  const moonCircle = new THREE.Mesh(
    new THREE.SphereGeometry(0.035, 10, 10),
    matEmissive(0xffffcc, 0.6),
  );
  moonCircle.position.set(0, 1.26, -0.21);
  group.add(moonCircle);

  enableShadows(group);
  return group;
}
